import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, Redirect } from 'react-router-dom';

import styled from '@emotion/styled-base';
import { colors } from '@modernhealth/vital-foundation';
import { ContentCard, pxToRem } from '@modernhealth/vital-components';
import { captureException } from '@sentry/browser';

import { appClient } from 'src/config/apolloV3';
import { useGetCompletedScheduledContentQuery } from 'src/modules/scheduledContent/documents/getCompletedScheduledContent.generated';
import { DailyPauseStatsSubHeader } from 'src/modules/scheduledContent/components/DailyPauseStatsHeader';
import { MinimizedCompletedDailyPauseSkeletons } from './DailyPauseSkeletons';
import { capture } from 'src/shared/helpers/sentryCapture';

const Container = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    flexWrap: 'wrap',
    gap: pxToRem(24),
    maxWidth: pxToRem(432),
    width: '100%',
});

const ContentCardWrapper = styled('div')({
    width: pxToRem(204),
    '& img': {
        objectFit: 'contain',
    },
});

export const MinimizedCompletedDailyPauses: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();

    const { data, loading, error } = useGetCompletedScheduledContentQuery({
        client: appClient,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
        variables: {
            first: 2, // fetch 2 recently finished DailyPauses
        },
        onError: capture,
    });

    const completedDailyPauses = data?.completedScheduledContent;

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    if (loading && !completedDailyPauses) {
        return <MinimizedCompletedDailyPauseSkeletons />;
    }

    if (!loading && !completedDailyPauses) {
        return <Redirect to="/dashboard" />;
    }

    const links = completedDailyPauses?.edges.slice(0, 2) || []; // slice first 2, to be sure that no more than 2 elements will be rendered

    return (
        <>
            <DailyPauseStatsSubHeader
                text={t('dailyPause:streaks.completed.title')}
                onClick={() => history.push('/pause/completed-list')}
            />
            {completedDailyPauses && (
                <Container>
                    {links?.map((link, i) => {
                        const node = link?.node;
                        if (!node) return null;
                        return (
                            <ContentCardWrapper key={`${node.id}`}>
                                <ContentCard
                                    title={node.bannerTitle}
                                    description={node.mediaType?.name || ''}
                                    image={node.bannerImage || ''}
                                    backgroundColor={
                                        node.accentColor ? `#${node.accentColor}` : colors.white
                                    }
                                    id={node.id}
                                    testID={`CompletedDailyPauseItem-${i}`}
                                    descriptionStartIconUrl={node.mediaType?.icon}
                                    isCompleted
                                    onClick={() => {
                                        history.push(`/pause/${node.uuid ?? ''}`);
                                    }}
                                />
                            </ContentCardWrapper>
                        );
                    })}
                </Container>
            )}
        </>
    );
};
